<script setup lang="ts">
import type { ApproveEmit, PopApproveProps } from '/#/components/base/approve'
import type { ComputedRef } from 'vue'

import { BODY } from '/@/constants/common'

const props = withDefaults(defineProps<PopApproveProps>(), {
  title: undefined,
  description: undefined,
  htmlDescription: false,
  approveButtonText: undefined,
  rejectButtonText: undefined,
  actionIsActive: true,
  trigger: 'click',
})

const emit = defineEmits<ApproveEmit>()

const { t } = useI18n()

/**
 * Popover title
 * @default 'pop-approve.default-title'
 */
const head: ComputedRef<string> = computed(
  () => props.title || t('pop-approve.default-title'),
)

/**
 * Popover description
 * @default 'pop-approve.default-description'
 */
const body: ComputedRef<string> = computed(
  () => props.description || t('pop-approve.default-description'),
)

/**
 * Approve button text
 * @default 'action.yes'
 */
const approveButtonText: ComputedRef<string> = computed(
  () => props.approveButtonText || t('action.yes'),
)

/**
 * Reject button text
 * @default 'action.no'
 */
const rejectButtonText: ComputedRef<string> = computed(
  () => props.rejectButtonText || t('action.no'),
)

const tippyWrapper = ref()

/**
 * This is for table row overflow visibility
 * The element to append the document body
 */
const tippyProps: ComputedRef<Recordable> = computed(() => ({
  appendTo: BODY,
  trigger: props.trigger,
  interactive: true,
  theme: 'pop-approve',
}))

function triggerApprove(hide: () => void) {
  emit('approve')
  hide()
}

function triggerReject(hide: () => void) {
  emit('reject')
  hide()
}

// Show dropdown programmatically.
function showApproveModal() {
  tippyWrapper.value.tippy.show()
}

// Hide dropdown programmatically.
function hideApproveModal() {
  tippyWrapper.value.tippy.hide()
}

/**
 * Expose dropdown functions.
 */
defineExpose({
  showApproveModal,
  hideApproveModal,
})
</script>

<template>
  <Tippy ref="tippyWrapper" v-bind="tippyProps">
    <template #default="state">
      <slot name="default" v-bind="state" />
    </template>
    <template #content="state">
      <div class="pop-approve-content">
        <slot name="content" v-bind="state">
          <h3 class="pop-approve-title">{{ head }}</h3>
          <p class="pop-approve-description">
            <span v-if="htmlDescription" v-sanitize="body" />
            <span v-else>{{ body }}</span>
          </p>
        </slot>
        <Flex
          v-if="actionIsActive"
          justify-content="end"
          align-items="center"
          column-gap="0.625rem"
          class="pop-approve-button"
        >
          <FlexItem>
            <Button
              color="slate"
              variant="outlined"
              :loading="loading"
             
              @click.prevent.stop="triggerReject(state.hide)"
            >
              {{ rejectButtonText }}
            </Button>
          </FlexItem>
          <FlexItem>
            <Button
              color="primary"
              :loading="loading"
              :disabled="disabled"
             
              @click.prevent.stop="triggerApprove(state.hide)"
            >
              {{ approveButtonText }}
            </Button>
          </FlexItem>
        </Flex>
      </div>
    </template>
  </Tippy>
</template>

<style lang="scss" scoped>
.pop-approve-content {
  padding: 1rem;
  z-index: 9999;
  position: relative;
}

.pop-approve-title {
  font-size: 1.125em;
  font-weight: 500;
  margin-bottom: 0.5em;
}

.pop-approve-description {
  font-size: 0.875em;
  max-width: 19rem;
  color: map-get($text-colors, 'muted');
}

.pop-approve-button {
  margin-top: 1rem;
}
</style>
